import { Lock, ChevronsDown } from "lucide-react";

/**
 * Read-only preview of the ten dungeon floors and their unlock requirements.
 * Rendered above the ClassSelection panel so players see the arc + gates before diving.
 * `floorGates` is `{floor: minLevel}` from the backend.
 */
export default function StageSelector({
  floorGates = {},
  bossFloors = [2, 4, 6, 8, 10],
  finalFloor = 10,
  playerLevel = 1,
}) {
  const chapters = [
    { range: [1, 2],  title: "THE FALLEN GATE",   chapter: "I" },
    { range: [3, 3],  title: "BOSS · GATEKEEPER", chapter: "II" },
    { range: [4, 5],  title: "THE ASHEN LIBRARY", chapter: "III" },
    { range: [6, 6],  title: "BOSS · ARCHIVIST",  chapter: "IV" },
    { range: [7, 9],  title: "THE INFERNAL FORGE",chapter: "V" },
    { range: [10, 10],title: "BOSS · EMBER KING", chapter: "VI" },
  ];
  const bossSet = new Set(bossFloors);

  return (
    <div className="panel p-5 mb-8" data-testid="stage-selector">
      <div className="flex items-center justify-between mb-4">
        <div className="text-[10px] tracking-[0.4em] ash-text flex items-center gap-2">
          <ChevronsDown size={12} className="ember" />
          THE DESCENT · {finalFloor} FLOORS
        </div>
        <div className="text-[10px] tracking-[0.3em] ash-text">
          FRESH SOULS BEGIN AT <span className="gold-text">LV 1</span>
        </div>
      </div>

      <div className="grid grid-cols-5 sm:grid-cols-10 gap-2">
        {Array.from({ length: finalFloor }, (_, i) => i + 1).map((f) => {
          const min = floorGates[String(f)] ?? floorGates[f] ?? 0;
          const locked = min > playerLevel;
          const isBoss = bossSet.has(f);
          return (
            <div
              key={f}
              data-testid={`floor-tile-${f}`}
              className="relative rounded-sm p-2 text-center overflow-hidden"
              style={{
                background: locked
                  ? "linear-gradient(180deg, rgba(20,10,6,0.9), rgba(6,3,2,0.95))"
                  : isBoss
                    ? "linear-gradient(180deg, rgba(90,20,10,0.55), rgba(30,8,4,0.85))"
                    : "linear-gradient(180deg, rgba(30,20,12,0.65), rgba(10,5,3,0.85))",
                border: `1px solid ${locked ? "rgba(120,90,50,0.25)" : isBoss ? "rgba(226,88,34,0.5)" : "rgba(212,175,55,0.35)"}`,
                boxShadow: isBoss && !locked ? "0 0 12px rgba(226,88,34,0.25) inset" : "none",
                opacity: locked ? 0.55 : 1,
              }}
              title={locked ? `Unlocks at Level ${min}` : `Floor ${f}${isBoss ? " · BOSS" : ""}`}
            >
              <div className="text-[8px] tracking-[0.3em] ash-text">FLOOR</div>
              <div className={`font-heading text-lg ${isBoss ? "text-[#e25822]" : "gold-text"}`}>
                {f}
              </div>
              {locked ? (
                <div
                  className="mt-1 text-[8px] tracking-[0.2em] flex items-center justify-center gap-1"
                  style={{ color: "#8b7965" }}
                  data-testid={`floor-lock-${f}`}
                >
                  <Lock size={8} /> LV {min}
                </div>
              ) : (
                <div className="mt-1 text-[8px] tracking-[0.2em] ash-text">
                  {isBoss ? "BOSS" : "OPEN"}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-4 pt-3 border-t border-white/10 flex flex-wrap gap-3 text-[10px] tracking-[0.3em] ash-text">
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-2 h-2" style={{ background: "#d4af37" }} /> OPEN
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-2 h-2" style={{ background: "#e25822" }} /> BOSS
        </span>
        <span className="flex items-center gap-1.5">
          <Lock size={10} className="text-[#8b7965]" /> LOCKED · LEVEL GATE
        </span>
        <span className="ml-auto italic opacity-70">
          Descend to unlock. Level up to break the seals.
        </span>
      </div>
    </div>
  );
}
